import { useQuery } from '@apollo/client';
import { Button, Spacer } from '@perch-labs/shrike';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { copyFor, routes } from 'config';
import { useAccountContext } from 'contexts/account';
import { useSessionContext } from 'contexts/session';
import { GET_ACCOUNT_SETTINGS_INFO } from 'pages/settings/components/account-settings/account-settings.api';
import { UtilityCredentialConnectButton } from 'pages/settings/secondary/utility-sync/components/utility-credential-connect-button';
import { useHistory } from 'react-router-dom';
import { Text } from 'styles';
import { GET_UTILITY_SYNC_CREDENTIAL_DATA } from 'pages/settings/secondary/utility-sync/utility-sync-credential/utility-sync-credential.api';
import { trackEvent } from 'domain/tracking';
import { useEffect } from 'react';
import { StyledHeading } from './connect-utility.style';
import { commonMovingPayload } from '../../state-machine/actions';

const getCopy = copyFor('settings.moving.connectUtility');

interface Props {
  context: MovingStateMachineContext;
}

export const ConnectUtility = ({ context }: Props) => {
  const { userId } = useSessionContext();
  const { language, selectedAccountId } = useAccountContext();
  const history = useHistory();

  useEffect(() => {
    trackEvent('moving_utility_connect_started', {
      metadata: commonMovingPayload(context),
    });
  }, []);

  const {
    data: accountData,
    error: accountError,
    loading: accountLoading,
  } = useQuery<
    GetAccountSettingsInfoQuery,
    GetAccountSettingsInfoQueryVariables
  >(GET_ACCOUNT_SETTINGS_INFO, {
    fetchPolicy: 'no-cache',
    variables: { accountId: selectedAccountId },
    skip: !selectedAccountId,
  });

  const newUtilityCredentials = accountData?.account?.utilityCredentials;
  const newUtilityCredential = newUtilityCredentials?.[0];
  const newUtilityCredentialId = newUtilityCredential?.id;

  const { data, loading, error } = useQuery<
    GetUtilitySyncCredentialDataQuery,
    GetUtilitySyncCredentialDataQueryVariables
  >(GET_UTILITY_SYNC_CREDENTIAL_DATA, {
    skip: !newUtilityCredentialId,
    variables: { utilityCredentialId: Number(newUtilityCredentialId) || -1 },
  });

  if (accountLoading || loading) return <LoadingPage />;
  if (accountError || error || !data) {
    return <ErrorPage title="Error connecting utility account" />;
  }

  const utilityCredential = data?.utilityCredential;

  const utility = utilityCredential?.utility;
  const state = utility?.state?.name;

  const handleSkipButtonClick = () => {
    trackEvent('moving_skip_connect_utility', {
      metadata: commonMovingPayload(context),
    });
    history.push(routes().dashboard);
  };

  return (
    <>
      <StyledHeading>{getCopy('nonVnmTitle')}</StyledHeading>
      <Text>
        {getCopy('nonVnmDescription', {
          state,
        })}
      </Text>
      {utilityCredential && utility ? (
        <UtilityCredentialConnectButton
          utilityCredentialId={utilityCredential.id}
          userName={utilityCredential.username}
          userId={userId}
          accountId={selectedAccountId}
          language={language}
          movingUser={true}
        />
      ) : null}
      <Spacer padding="24px 0" innerText="or" />
      <Button fullWidth backgroundless onClick={handleSkipButtonClick}>
        {getCopy('skipButton')}
      </Button>
    </>
  );
};
